import type { CSSProperties, ReactNode } from "react";

/**
 * "G · Engraved double line" from the restaurant page lab (owner, 2026-09-26):
 * two very light lines with white between, like an engraved plate. The face
 * carries the pewter sheen itself ("gradient inside"), lightened so text
 * stays on a near-white. The shadow is kept to 14px because inside a
 * sideways scroll row a wide shadow is cut off at the row's edges and reads
 * as a grey box.
 */
export const ENGRAVED: CSSProperties = {
  border: "1px solid rgba(17,14,57,0.06)",
  background:
    "linear-gradient(155deg, #FFFFFF 0%, #F3F4F9 35%, #E9ECF4 62%, #F4F5F9 85%, #FFFFFF 100%)",
  boxShadow:
    "inset 0 0 0 3px #FFFFFF, inset 0 0 0 4px rgba(17,14,57,0.05), 0 5px 14px rgba(17,14,57,0.05)",
};

/**
 * The same plate as a reading panel (menu, map, hours). Larger surfaces keep
 * the gradient but flatten it towards white, so a long dish list is not read
 * across a grey band.
 */
const ENGRAVED_PANEL: CSSProperties = {
  ...ENGRAVED,
  background:
    "linear-gradient(155deg, #FFFFFF 0%, #F7F8FB 40%, #F1F3F8 70%, #FFFFFF 100%)",
};

export function EngravedPanel({
  children,
  className,
  as: Tag = "section",
  style,
  ...aria
}: {
  children: ReactNode;
  className?: string;
  as?: "section" | "div";
  /** Replaces the engraved face, e.g. with the pewter skin's frame. */
  style?: CSSProperties;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}) {
  return (
    <Tag className={`rounded-[22px] ${className ?? ""}`} style={style ?? ENGRAVED_PANEL} {...aria}>
      {children}
    </Tag>
  );
}

/**
 * A soft fade on the trailing edge of a horizontal scroll row, so a cut-off
 * tile reads as "more this way". Page-background coloured (#faf9fd), and
 * flipped in RTL where the row runs the other way.
 */
export function TrailingFade() {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-y-0 end-0 w-10 bg-linear-to-l from-[#faf9fd] to-transparent rtl:bg-linear-to-r"
    />
  );
}
